import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { api } from '../api';
import { X, ImageOff, History, Boxes, ChevronRight } from 'lucide-react';

export default function ItemDetailModal({ item, onClose }) {
  const { itemAvailableQty, itemStatus, projects, categoryColor } = useApp();
  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!item) {
      setHistory([]);
      return;
    }
    setLoadingHistory(true);
    api
      .listTransactions({ item_code: item.code, limit: 10 })
      .then((tx) => {
        if (!cancelled) setHistory(tx || []);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });
    return () => {
      cancelled = true;
    };
  }, [item]);

  if (!item) return null;

  const avail = itemAvailableQty(item.code);
  const status = itemStatus(item.code);

  const outOnProjects = projects
    .filter((p) => p.status === 'active' && (p.packed?.[item.code] || 0) > 0)
    .map((p) => ({ project: p, qty: p.packed[item.code] }));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="surface rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-neutral-900">
          <div className="flex items-start gap-4 min-w-0">
            {!imgErr && item.image ? (
              <img
                src={item.image}
                alt={item.name}
                onError={() => setImgErr(true)}
                className="h-16 w-24 object-cover rounded border border-neutral-800 shrink-0"
              />
            ) : (
              <div className="h-16 w-24 rounded bg-neutral-900 border border-neutral-800 grid place-items-center text-neutral-600 shrink-0">
                <ImageOff size={20} />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-0.5 font-mono">
                {item.code}
              </div>
              <h2 className="text-lg font-bold text-white leading-snug mb-1">{item.name}</h2>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="cat-dot" style={{ background: categoryColor(item.category) }} />
                <span className="text-neutral-400">{item.category}</span>
                <span className="text-neutral-700">·</span>
                <span className="text-neutral-400">{item.location}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white p-1">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-5">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-md border border-neutral-900 bg-neutral-950">
              <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1">Total</div>
              <div className="text-2xl font-bold text-white">{item.totalQty}</div>
            </div>
            <div className="p-3 rounded-md border border-neutral-900 bg-neutral-950">
              <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1">Available</div>
              <div className="text-2xl font-bold text-emerald-400">{avail}</div>
            </div>
            <div className="p-3 rounded-md border border-neutral-900 bg-neutral-950">
              <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1">Status</div>
              <div className="text-sm text-neutral-200 capitalize mt-1">{status.replace('-', ' ')}</div>
            </div>
          </div>

          {/* QR Code */}
          <div>
            <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-1.5">QR / Scan Code</div>
            <div className="font-mono text-sm text-white bg-neutral-950 border border-neutral-800 rounded-md px-3 py-2 inline-block">
              {item.qrCode || item.code}
            </div>
          </div>

          {/* Out on projects */}
          {outOnProjects.length > 0 && (
            <div>
              <div className="text-[10px] tracking-wider uppercase text-neutral-500 mb-2 flex items-center gap-1.5">
                <Boxes size={11} /> Currently on these projects
              </div>
              <div className="space-y-1.5">
                {outOnProjects.map(({ project, qty }) => (
                  <button
                    key={project.id}
                    onClick={() => {
                      navigate(`/projects/${project.id}`);
                      onClose();
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-neutral-900 bg-neutral-950 hover:bg-neutral-900 text-left"
                  >
                    <span className="text-sm text-white">{project.name}</span>
                    <span className="text-[11px] text-amber-400 font-medium">
                      {qty} unit{qty === 1 ? '' : 's'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* History */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] tracking-wider uppercase text-neutral-500 flex items-center gap-1.5">
                <History size={11} /> Recent activity
              </div>
              <button
                onClick={() => {
                  navigate(`/inventory/${item.code}`);
                  onClose();
                }}
                className="text-[11px] text-amber-400 hover:text-amber-300 flex items-center gap-0.5"
              >
                Full history <ChevronRight size={11} />
              </button>
            </div>
            {loadingHistory ? (
              <div className="text-xs text-neutral-600 py-3">Loading…</div>
            ) : history.length === 0 ? (
              <div className="text-xs text-neutral-600 py-3">No transactions yet.</div>
            ) : (
              <div className="space-y-1">
                {history.map((t) => {
                  const proj = projects.find((p) => p.id === t.projectId);
                  return (
                    <div
                      key={t.id}
                      className="flex items-center gap-3 text-xs px-3 py-2 border border-neutral-900 rounded-md bg-neutral-950"
                    >
                      <span
                        className={`text-[10px] uppercase tracking-wider font-medium w-14 ${
                          t.type === 'pack' ? 'text-amber-400' : 'text-emerald-400'
                        }`}
                      >
                        {t.type}
                      </span>
                      <span className="text-neutral-400 flex-1 truncate">{proj?.name || '—'}</span>
                      <span className="text-neutral-600">{new Date(t.ts).toLocaleString()}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
